// Room state queries for media elements

import { useCallback, useMemo } from 'react';
import { useRoomStateSelector } from './useRoomStateSelector';
import { useRoomGameElements } from './useRoomGameElements';
import { MediaType, MediaActionType } from '@airhead-games/shared/types';

export function useRoomMediaElements() {
  // --- Room State Access ---
  const mediaElements = useRoomStateSelector(
    (state) => state?.mediaElements || {}
  );

  // --- Game Elements Access ---
  const { turnInProgress } = useRoomGameElements();

  // =============================================================================
  // ROOM STATE QUERIES
  // =============================================================================
  // Helper functions for accessing and querying media elements from room state

  /**
   * Get media elements filtered by various criteria
   * @param options - Filter options
   * @param options.mediaIds - Optional array of specific media ID(s) to get
   * @param options.mediaType - Optional media type to filter by
   * @param options.mediaAction - Optional media action to filter by
   * @param options.elementIds - Optional array of element ID(s) to filter by
   * @param options.content - Optional content to filter by
   * @returns Array of MediaElements matching the filters
   * @example
   * // Get specific element: getMediaElements({ mediaIds: ['music-123'] })[0]
   * // Get all speeches that are completed: getMediaElements({ mediaType: MediaType.SPEECH, mediaAction: MediaActionType.COMPLETED })
   * // Get all images for specific turns: getMediaElements({ mediaType: MediaType.IMAGE, elementIds: ['turn-123', 'turn-124'] })
   */
  const getMediaElements = useCallback(
    (
      options: {
        mediaIds?: string[];
        mediaType?: MediaType;
        mediaAction?: MediaActionType;
        elementIds?: string[];
        content?: string;
      } = {}
    ) => {
      // If mediaIds array is specified, return those elements
      if (options.mediaIds) {
        return options.mediaIds
          .map((id) => mediaElements[id])
          .filter((element) => element !== undefined);
      }

      // Otherwise filter by type, action, and/or elementIds
      return Object.values(mediaElements).filter((element) => {
        if (options.mediaType && element.mediaType !== options.mediaType) {
          return false;
        }
        if (
          options.mediaAction &&
          element.mediaAction !== options.mediaAction
        ) {
          return false;
        }
        if (
          options.elementIds &&
          (!element.elementId ||
            !options.elementIds.includes(element.elementId))
        ) {
          return false;
        }
        if (options.content && element.content !== options.content) {
          return false;
        }
        return true;
      });
    },
    [mediaElements]
  );

  /**
   * Get the last media element by type (highest mediaId / last in order)
   * @param mediaType - The media type to filter by
   * @returns The last MediaElement of that type or undefined if none found
   */
  const getLastMediaElement = useCallback(
    (mediaType: MediaType) => {
      const elements = getMediaElements({ mediaType });
      // Elements are added in order, so the last one is the latest
      return elements.length > 0 ? elements[elements.length - 1] : undefined;
    },
    [getMediaElements]
  );

  /**
   * Get the nth media element matching the specified criteria
   * @param n - The position (1-indexed) of the element to retrieve
   * @param options - Filter options (same as getMediaElements)
   * @returns The nth MediaElement matching the criteria or undefined if not found
   * @example
   * // Get the 2nd speech for a specific elementId:
   * getNthMediaElement(2, { mediaType: MediaType.SPEECH, elementIds: ['turn-123'] })
   * // Get the 3rd completed image:
   * getNthMediaElement(3, { mediaType: MediaType.IMAGE, mediaAction: MediaActionType.COMPLETED })
   */
  const getNthMediaElement = useCallback(
    (
      n: number,
      options: {
        mediaIds?: string[];
        mediaType?: MediaType;
        mediaAction?: MediaActionType;
        elementIds?: string[];
        content?: string;
      } = {}
    ) => {
      if (n < 1) return undefined;

      const elements = getMediaElements(options).sort((a, b) =>
        a.mediaId.localeCompare(b.mediaId)
      );

      return elements[n - 1]; // 1-indexed
    },
    [getMediaElements]
  );

  /**
   * Get a signed URL from room state for any media type
   * @param mediaId - The media element ID
   * @param mediaType - Type of media to get URL for
   * @returns The signed URL(s) - string for most types, object for MUSIC type with both musicUrl and imageUrl
   */
  const getMediaUrl = useCallback(
    (
      mediaId: string,
      mediaType: MediaType
    ): string | { musicUrl?: string; imageUrl?: string } | undefined => {
      const element = mediaElements[mediaId];
      if (!element) return undefined;

      switch (mediaType) {
        case MediaType.IMAGE:
        case MediaType.BACKGROUND:
          return element.imageUrl;
        case MediaType.MUSIC:
          return {
            musicUrl: element.musicUrl,
            imageUrl: element.imageUrl, // Album art
          };
        case MediaType.SFX:
          return element.sfxUrl;
        default:
          return undefined;
      }
    },
    [mediaElements]
  );

  /**
   * Count the number of completed speeches
   * @param elementId - Optional element ID to filter speeches for a specific turn/element
   * @returns The number of speech elements with COMPLETED action
   * @example
   * // Count all completed speeches:
   * const allCompletedSpeeches = countCompletedSpeeches();
   *
   * // Count completed speeches for a specific turn:
   * const turnCompletedSpeeches = countCompletedSpeeches(turnId);
   * const isSpeech5Completed = turnCompletedSpeeches >= 5;
   */
  const countCompletedSpeeches = useCallback(
    (elementId?: string): number => {
      const completedSpeeches = getMediaElements({
        mediaType: MediaType.SPEECH,
        mediaAction: MediaActionType.COMPLETED,
        ...(elementId && { elementIds: [elementId] }),
      });
      return completedSpeeches.length;
    },
    [getMediaElements]
  );
  /**
   * Count completed speeches for the latest turn
   * @returns The number of speech elements with COMPLETED action for the latest turn
   */
  const speechesCompletedForTurnInProgress = countCompletedSpeeches(
    turnInProgress?.elementId || undefined
  );

  /**
   * Count the number of speeches that have at least started
   * @param elementId - Optional element ID to filter speeches for a specific turn/element
   * @returns The number of speech elements that are not READY (i.e., STARTED or COMPLETED)
   * @example
   * // Count all speeches that have at least started:
   * const allStartedSpeeches = countStartedSpeeches();
   *
   * // Count speeches that have at least started for a specific turn:
   * const turnStartedSpeeches = countStartedSpeeches(turnId);
   * const isSpeech3Started = turnStartedSpeeches >= 3;
   */
  const countStartedSpeeches = useCallback(
    (elementId?: string): number => {
      const startedSpeeches = getMediaElements({
        mediaType: MediaType.SPEECH,
        mediaAction: MediaActionType.STARTED,
        ...(elementId && { elementIds: [elementId] }),
      });
      const completedSpeeches = getMediaElements({
        mediaType: MediaType.SPEECH,
        mediaAction: MediaActionType.COMPLETED,
        ...(elementId && { elementIds: [elementId] }),
      });
      return startedSpeeches.length + completedSpeeches.length;
    },
    [getMediaElements]
  );

  /**
   * Count speeches that have at least started for the latest turn
   * @returns The number of speech elements that are not READY for the latest turn
   */
  const speechesStartedForTurnInProgress = countStartedSpeeches(
    turnInProgress?.elementId || undefined
  );

  /**
   * Get all unfinished speeches (READY and STARTED)
   * Memoized computation for speech recovery purposes
   */
  const unfinishedSpeeches = useMemo(() => {
    const readySpeeches = getMediaElements({
      mediaType: MediaType.SPEECH,
      mediaAction: MediaActionType.READY,
    });

    const startedSpeeches = getMediaElements({
      mediaType: MediaType.SPEECH,
      mediaAction: MediaActionType.STARTED,
    });

    return [...readySpeeches, ...startedSpeeches];
  }, [getMediaElements]);

  // =============================================================================
  // PUBLIC API
  // =============================================================================

  return {
    // Room state query functions
    getMediaUrl,
    getMediaElements,
    getLastMediaElement,
    getNthMediaElement,
    countCompletedSpeeches,
    speechesCompleted: speechesCompletedForTurnInProgress,
    countStartedSpeeches,
    speechesStarted: speechesStartedForTurnInProgress,
    unfinishedSpeeches,
  };
}
